import type { SendNextStepNotificationRequestModel } from './types'
import type { GroupStatus, PersonalDataFormInputPayload } from '@/domain/groups/types'
import type { APIBookingDraft, APIStepsTabs } from '@/scopes/checkout/infrastructure/api/types'
import defaultFetch from 'isomorphic-unfetch'
import { BookingToCheckoutParameters } from '@/domain/groups/api'
import { ParticipantsFormData } from '@/scopes/checkout/domain/types/checkoutPersonalDetails'
import { PreferencesFormData } from '@/scopes/checkout/domain/types/checkoutPreferences'
import {
  CheckoutParticipantsDetailsMapper,
  createCheckoutParticipantsDetailsMapper,
} from '@/scopes/checkout/infrastructure/api/checkoutParticipantsDetailsMapper'
import { createBookingDraftErrorMapper } from '@/scopes/checkout/infrastructure/api/model-mappers/bookingDraftErrorMapper'
import {
  createParticipantsDetailsFormDataMapper,
  ParticipantsDetailsFormDataMapper,
} from '@/scopes/checkout/infrastructure/api/participantsDetailsFormDataMapper'
import {
  createPreferencesFormDataMapper,
  PreferencesFormDataMapper,
} from '@/scopes/checkout/infrastructure/api/preferencesFormDataMapper'
import { serviceMappers as defaultServiceMappers } from '@/scopes/checkout/infrastructure/api/serviceMappers'
import {
  createTransformBookingToCheckoutMapper,
  TransformBookingToCheckoutDataMapper,
} from '@/scopes/checkout/infrastructure/api/transformBookingToCheckoutMapper'
import {
  createQueryStringSerializer,
  type QueryStringSerializer,
} from '@/scopes/common/infrastructure/query-strings/queryStringSerializer'
import {
  ApiContextParameters,
  BookingDraftConfigurationParameters,
  BookingDraftCouponCodeParameters,
  BookingDraftEngineErrorParameters,
  BookingDraftParameters,
  BookingStepsTabsParameters,
  NextStepNotificationParameters,
  ServiceMappers,
  Services,
  ServiceType,
} from '../../domain/types'
import { BookingDraftResponseMapper, createBookingDraftResponseMapper } from './bookingDraftResponseMapper'
import { type BookingStepsResponseMapper, createBookingStepsResponseMapper } from './bookingStepsResponseMapper'
import { createRyanairSessionResponseMapper, type RyanairSessionResponseMapper } from './ryanairSessionResponseMapper'

interface Dependencies {
  env: {
    NEXT_PUBLIC_API_BASE_URL: string
    NEXT_PUBLIC_API_URL: string
  }
  fetch: typeof defaultFetch
  bookingDraftResponseMapper?: BookingDraftResponseMapper
  bookingStepsResponseMapper: BookingStepsResponseMapper
  queryStringSerializer: QueryStringSerializer
  serviceMappers: ServiceMappers
  checkoutParticipantsDetailsMapper?: CheckoutParticipantsDetailsMapper
  participantsDetailsFormDataMapper?: ParticipantsDetailsFormDataMapper
  preferencesFormDataMapper?: PreferencesFormDataMapper
  ryanairSessionResponseMapper?: RyanairSessionResponseMapper
  transformBookingToCheckoutMapper?: TransformBookingToCheckoutDataMapper
  apiContextParameters?: ApiContextParameters
}

export function createCheckoutAPIClient(
  dependencies: Partial<Dependencies> & Pick<Dependencies, 'bookingDraftResponseMapper'>,
) {
  const errorMapper = createBookingDraftErrorMapper()
  const {
    env = {
      NEXT_PUBLIC_API_BASE_URL: process.env.NEXT_PUBLIC_API_BASE_URL,
      NEXT_PUBLIC_API_URL: process.env.NEXT_PUBLIC_API_URL,
    },
    fetch = defaultFetch,
    queryStringSerializer = createQueryStringSerializer(),
    bookingStepsResponseMapper = createBookingStepsResponseMapper(),
    bookingDraftResponseMapper = createBookingDraftResponseMapper({
      serviceMappers: defaultServiceMappers,
      errorMapper,
    }),
    serviceMappers = defaultServiceMappers,
    checkoutParticipantsDetailsMapper = createCheckoutParticipantsDetailsMapper(),
    participantsDetailsFormDataMapper = createParticipantsDetailsFormDataMapper(),
    preferencesFormDataMapper = createPreferencesFormDataMapper(),
    ryanairSessionResponseMapper = createRyanairSessionResponseMapper({ errorMapper }),
    transformBookingToCheckoutMapper = createTransformBookingToCheckoutMapper(),
    apiContextParameters,
  } = dependencies as Dependencies

  async function getBookingDraft(
    draftParameters: BookingDraftParameters,
    configParameters?: BookingDraftConfigurationParameters,
  ) {
    let bookingDraftResponseData: APIBookingDraft
    try {
      const url = buildBookingDraftEndpointUrl(draftParameters)
      // Changed default referrer policy to 'no-referrer-when-downgrade' to ensure as referer we have the origin path doing the request.
      // This is needed as GetBookingDraft endpoint it's on another domain api.*
      const response = await fetch(url, { credentials: 'include', referrerPolicy: 'no-referrer-when-downgrade' })
      if (!response.ok) {
        throw new Error(`Failed to fetch booking draft: ${response.status} ${response.statusText}`)
      }
      bookingDraftResponseData = await response.json()
    }
    catch (error) {
      bookingDraftResponseData = {
        error: {
          hasError: true,
          errorMessage: (error as Error).message,
          description: 'Booking not found',
        },
        draftId: '',
        services: [],
        participants: [],
        rooms: [],
        isRebooking: false,
        conditionalMessages: [],
      }
    }
    return bookingDraftResponseMapper.map(bookingDraftResponseData, configParameters?.configData)
  }

  async function getStepsTabs(draftParameters: BookingStepsTabsParameters) {
    const url = buildStepsTabsEndpointUrl(draftParameters)
    const response = await fetch(url)

    const responseData: APIStepsTabs = await response.json()

    return bookingStepsResponseMapper.map(responseData)
  }

  async function setServiceSelection<K extends ServiceType>(
    apiContext: BookingDraftParameters,
    serviceType: K,
    updatedService: Services[K],
  ) {
    const serviceMapper = serviceMappers[serviceType]
    const typeOfService = serviceMapper.getServiceTypeMapper()
    const serviceSelection = serviceMapper.mapSelection(updatedService)
    const url = buildChangeServiceEndpointUrl(apiContext)

    await fetch(url, {
      method: 'PATCH',
      body: JSON.stringify({
        id: updatedService.id,
        typeOfService,
        data: serviceSelection,
      }),
    })
  }

  async function setParticipantsInformation(
    apiContext: BookingDraftParameters,
    payload: { id: string, name: string, birthdate?: string }[],
  ) {
    const url = buildSetParticipantsBadgeEndpointUrl(apiContext)
    await fetch(url, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        participants: payload,
      }),
    })
  }

  async function setGroupParticipantsInformation(
    apiContext: BookingDraftParameters,
    payload: PersonalDataFormInputPayload[],
  ) {
    const url = buildSetParticipantsDetailsEndpointUrl(apiContext)
    return fetch(url, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        participants: payload,
      }),
    })
  }

  async function setCouponCode(apiContext: BookingDraftCouponCodeParameters) {
    const url = buildSetCouponCodeEndpointUrl(apiContext)
    const response = await fetch(url, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
    })

    const responseData: APIBookingDraft = await response.json()

    return bookingDraftResponseMapper.map(responseData)
  }

  async function sendEngineErrorContactInfo(apiContext: BookingDraftEngineErrorParameters) {
    const url = buildEngineErrorContactInfoEndpointUrl(apiContext)
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        customerName: apiContext.customerName,
        customerEmail: apiContext.customerEmail,
        customerPhone: apiContext.customerPhone,
      }),
    })

    return response.ok
  }

  async function notifyNextStep({ cookieConsent, ...apiContext }: NextStepNotificationParameters) {
    const url = buildNextStepNotificationEndpointUrl(apiContext)
    const requestModel: SendNextStepNotificationRequestModel = { cookieConsent }
    const response = await fetch(url, {
      method: 'POST',
      credentials: 'include',
      keepalive: true,
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestModel),
    })

    return response.ok
  }

  async function transformBookingToCheckout(apiContext: BookingToCheckoutParameters) {
    const url = buildTransformBookingToCheckoutEndpointUrl(apiContext)

    const response = await fetch(url, { credentials: 'include' })
    if (!response.ok) {
      throw new Error(`Failed to transform booking to checkout: ${response.status} ${response.statusText}`)
    }
    const responseData: APIBookingDraft = await response.json()
    return bookingDraftResponseMapper.map(responseData)
  }

  async function transformBookingToCheckoutAsPost(apiContext: BookingToCheckoutParameters, payloadData?: GroupStatus) {
    const url = buildTransformBookingToCheckoutEndpointUrl(apiContext)
    const mappedPayload = transformBookingToCheckoutMapper.map(payloadData)
    const response = await fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(mappedPayload),
    })
    if (!response.ok) {
      throw new Error(`Failed to transform booking to checkout: ${response.status} ${response.statusText}`)
    }
    const responseData: APIBookingDraft = await response.json()
    return bookingDraftResponseMapper.map(responseData)
  }

  async function getParticipantsDetailsFormData(sid: string) {
    if (!apiContextParameters) {
      throw new Error('API context parameters are not provided')
    }
    const { contextItemId, participantsFormContextItemId } = apiContextParameters

    if (!participantsFormContextItemId) {
      throw new Error('Participants form context item id is not provided')
    }
    const participantsFormUrl = buildGetParticipantsFormDataEndpointUrl({
      contextItemId: participantsFormContextItemId,
      sid,
    })

    const bookingDraftUrl = buildBookingDraftEndpointUrl({
      contextItemId,
      sid,
    })

    const responses = await Promise.all([
      fetch(participantsFormUrl, { credentials: 'include' }),
      fetch(bookingDraftUrl, { credentials: 'include' }),
    ]).then(([participantsFormResponse, bookingDraftResponse]) => {
      if (!participantsFormResponse.ok) {
        throw new Error(
          `Failed to fetch participants form data: ${participantsFormResponse.status} ${participantsFormResponse.statusText}`,
        )
      }
      if (!bookingDraftResponse.ok) {
        throw new Error(
          `Failed to fetch booking draft data: ${bookingDraftResponse.status} ${bookingDraftResponse.statusText}`,
        )
      }
      return [participantsFormResponse, bookingDraftResponse]
    })

    const [participantsFormData, bookingDraftData] = await Promise.all(responses.map(response => response.json()))

    return {
      participantDetails: checkoutParticipantsDetailsMapper.map(participantsFormData, bookingDraftData),
      bookingDraft: bookingDraftResponseMapper.map(bookingDraftData),
    }
  }

  async function submitParticipantsFormData(sid: string, data: ParticipantsFormData) {
    if (!apiContextParameters) {
      throw new Error('API context parameters are not provided')
    }
    const { contextItemId } = apiContextParameters

    const setParticipantsFormUrl = buildSetParticipantsDetailsEndpointUrl({
      contextItemId,
      sid,
    })

    const payloadMapped = participantsDetailsFormDataMapper.map(data)

    const response = await fetch(setParticipantsFormUrl, {
      method: 'PATCH',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payloadMapped),
    })

    if (!response.ok) {
      throw new Error(`Failed to submit participants form data: ${response.status} ${response.statusText}`)
    }

    const jsonResponse = await response.json()

    if (jsonResponse.error && jsonResponse.error.hasError) {
      throw new Error('Failed to validate participants')
    }

    return jsonResponse
  }

  async function submitPreferencesData(sid: string, data: PreferencesFormData) {
    if (!apiContextParameters) {
      throw new Error('API context parameters are not provided')
    }
    const { contextItemId } = apiContextParameters

    const setPreferencesUrl = buildSetPreferencesEndpointUrl({
      contextItemId,
      sid,
    })

    const payloadMapped = preferencesFormDataMapper.map(data)

    const response = await fetch(setPreferencesUrl, {
      method: 'PATCH',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payloadMapped),
    })

    if (!response.ok) {
      throw new Error(`Failed to submit preferences data: ${response.status} ${response.statusText}`)
    }

    const jsonResponse = await response.json()

    if (jsonResponse.error && jsonResponse.error.hasError) {
      throw new Error('Failed to validate preferences')
    }

    return jsonResponse
  }

  async function getRyanairSessionData({ sid, contextItemId, testdata, debug }: BookingDraftParameters) {
    const url = buildGetRyanairSessionDataEndpointUrl({ sid, contextItemId, testdata, debug })
    const response = await fetch(url, { credentials: 'include' })

    if (!response.ok) {
      throw new Error(`Failed to fetch Ryanair session data: ${response.status} ${response.statusText}`)
    }

    const responseData: APIBookingDraft = await response.json()
    return ryanairSessionResponseMapper.map(responseData)
  }

  function buildGetParticipantsFormDataEndpointUrl({ contextItemId, sid }: BookingDraftParameters) {
    const queryString = queryStringSerializer.serialize({
      sid,
      contextItemId,
      stepTimeout: '30000',
    })

    return `${env.NEXT_PUBLIC_API_BASE_URL}/ParticipantsForm/GetParticipantsFormData?${queryString}`
  }

  function buildBookingDraftEndpointUrl({ contextItemId, sid, debug, testdata, synthetic }: BookingDraftParameters) {
    const queryString = queryStringSerializer.serialize({
      sid,
      contextItemId,
      debug,
      stepTimeout: '30000',
      testdata,
      synthetic,
    })

    return `${env.NEXT_PUBLIC_API_URL}/BookingDraft/GetBookingDraft?${queryString}`
  }

  function buildStepsTabsEndpointUrl({ contextItemId, sid }: BookingStepsTabsParameters) {
    const queryString = queryStringSerializer.serialize({
      sid,
      contextItemId,
      stepTimeout: '30000',
    })

    return `${env.NEXT_PUBLIC_API_BASE_URL}/StepNavigation/GetStepTabs?${queryString}`
  }

  function buildChangeServiceEndpointUrl({ contextItemId, sid, debug, testdata }: BookingDraftParameters) {
    const queryString = queryStringSerializer.serialize({
      sid,
      contextItemId,
      stepTimeout: '30000',
      debug,
      testdata,
    })
    return `${env.NEXT_PUBLIC_API_BASE_URL}/BookingDraft/ChangeServices?${queryString}`
  }

  function buildSetParticipantsBadgeEndpointUrl({ contextItemId, sid, debug, testdata }: BookingDraftParameters) {
    const queryString = queryStringSerializer.serialize({
      sid,
      contextItemId,
      debug,
      stepTimeout: '30000',
      testdata,
    })

    return `${env.NEXT_PUBLIC_API_BASE_URL}/BookingDraft/SetParticipantBadges?${queryString}`
  }

  function buildSetCouponCodeEndpointUrl({ contextItemId, sid, couponCode }: BookingDraftCouponCodeParameters) {
    const queryString = queryStringSerializer.serialize({
      sid,
      contextItemId,
      stepTimeout: '120000',
      couponCode,
    })

    return `${env.NEXT_PUBLIC_API_BASE_URL}/BookingDraft/ApplyCouponCode?${queryString}`
  }

  function buildEngineErrorContactInfoEndpointUrl({ contextItemId, sid }: BookingDraftParameters) {
    const queryString = queryStringSerializer.serialize({
      sid,
      contextItemId,
      stepTimeout: '120000',
    })

    return `${env.NEXT_PUBLIC_API_BASE_URL}/BookingDraft/SendInfoToContactCenter?${queryString}`
  }

  function buildNextStepNotificationEndpointUrl({ sid, contextItemId, stepNumber }: Omit<NextStepNotificationParameters, 'cookieConsent'>) {
    const queryString = queryStringSerializer.serialize({
      sid,
      contextItemId,
      stepNumber,
    })

    return `${env.NEXT_PUBLIC_API_BASE_URL}/BookingDraft/NextStepNotification?${queryString}`
  }

  function buildSetParticipantsDetailsEndpointUrl({ contextItemId, sid }: BookingDraftParameters) {
    const queryString = queryStringSerializer.serialize({
      sid,
      contextItemId,
      stepTimeout: '30000',
    })

    return `${env.NEXT_PUBLIC_API_BASE_URL}/BookingDraft/SetParticipants?${queryString}`
  }

  function buildSetPreferencesEndpointUrl({ contextItemId, sid }: BookingDraftParameters) {
    const queryString = queryStringSerializer.serialize({
      sid,
      contextItemId,
      stepTimeout: '30000',
    })

    return `${env.NEXT_PUBLIC_API_BASE_URL}/BookingDraft/SetPreferences?${queryString}`
  }

  function buildTransformBookingToCheckoutEndpointUrl({
    contextItemId,
    bookingNumber,
    groupUserId,
  }: BookingToCheckoutParameters) {
    const queryString = queryStringSerializer.serialize({
      bookingNumber,
      groupUserId,
      contextItemId,
      stepTimeout: '30000',
    })

    return `${env.NEXT_PUBLIC_API_URL}/BookingToCheckout/TransformBookingToCheckout?${queryString}`
  }

  function buildGetRyanairSessionDataEndpointUrl({ contextItemId, sid, debug, testdata }: BookingDraftParameters) {
    const queryString = queryStringSerializer.serialize({
      sid,
      contextItemId,
      debug,
      testdata,
      stepTimeout: '30000',
    })

    return `${env.NEXT_PUBLIC_API_URL}/BookingDraft/GetRyanairSessionData?${queryString}`
  }

  return {
    getBookingDraft,
    setServiceSelection,
    getStepsTabs,
    setParticipantsInformation,
    setGroupParticipantsInformation,
    transformBookingToCheckout,
    transformBookingToCheckoutAsPost,
    setCouponCode,
    sendEngineErrorContactInfo,
    notifyNextStep,
    getParticipantsDetailsFormData,
    submitParticipantsFormData,
    submitPreferencesData,
    getRyanairSessionData,
  }
}
